import { usePopup } from '@contexts/popupContext';
import styles from '@styles/components/QontactPoints/QontactPointListado.module.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@shared/designSystem/Loader';

import { ReactComponent as BinSVG } from '@assets/SVGs/Colaborador/BinSVG.svg';
import { ReactComponent as SearchSVG } from '@assets/SVGs/PresenteeismTable/SearchSVG.svg';
import { ReactComponent as EditSVG } from '@assets/SVGs/QontactPoint/EditOrangeSVG.svg';
import { ReactComponent as MarkerSVG } from '@assets/SVGs/QontactPoint/MarkerSVG.svg';
import { ReactComponent as QontactPointSVG } from '@assets/SVGs/QontactPoint/QontactPointSVG.svg';
import { ReactComponent as ViewSVG } from '@assets/SVGs/QontactPoint/ViewSVG.svg';

import ResultPopup from '@components/General/ResultPopup';
import QontactPointDeletePopup from './QontactPointDeletePopup';
import QontactPointDetailModalScreen from '@screens/QontactPoints/QontactPointDetailModalScreen';
import { QRMode } from '@screens/QontactPoints/QontactPointDetailModalViewModel';

import { APIIntegration, apiConfig } from '@integrations/APIIntegration';
import { VirtualizedList } from '@components/General/VirtualizedList';
import {LiteVersionPopup} from '@components/General/ProhibitedPopups';
import { Application } from '@core/Application';


interface QontactPoint {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    buildingID: string;
    functionalUnitID: string;
    totalShifts: number;
    triggerDistance?: number;
    shifts: any[];
}

interface QontactPointListadoProps {
    orgID: string;
    isLite?: boolean; // Optional prop to determine if the response is Lite or not
    qrMode?: QRMode; // Which check-in QRs the detail modal shows (org feature flag)
    shiftV2?: boolean; // Optional prop to determine if the response is V2 or V1
}


interface QontactPointRowProps {
    qontactPoint: QontactPoint;
    style: React.CSSProperties;
    index: number;
    isLite?: boolean;
    showPopup: any;
    handleNavigation: (path: string, state: any) => void;
    handleViewQontactPoint: (qontactPoint: QontactPoint) => void;
    handleDeleteQontactPoint: (qontactPoint: QontactPoint) => void;
}

const QontactPointRow: React.FC<QontactPointRowProps> = ({ qontactPoint, style, isLite, showPopup, index, handleNavigation, handleViewQontactPoint, handleDeleteQontactPoint }) => {
    return (
        <div className={styles['qontact-point-container']} style={index % 2 === 0 ? {...style, background: '#FBFBFB' } : {...style, background: '#FFF' }} key={index}>
            <div className={styles['qontact-point-info']}>
                <div className={styles['qontact-point-image-container']}>
                    <QontactPointSVG />
                </div>
                <div className={styles['qontact-point-info-column']}>
                    <p className={styles['qontact-point-name']}>{qontactPoint.name}</p>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '8px', height: '8px', marginRight: '9px' }}>
                            <MarkerSVG />
                        </div>
                        <p style={{ fontSize: '14px', color: '#6F7A89' }}>
                            {qontactPoint.latitude ? qontactPoint.latitude.toString().substring(0, 8) : "Sin latitud"}, {qontactPoint.longitude ? qontactPoint.longitude.toString().substring(0, 8) : "Sin longitud"}
                        </p>
                    </div>
                </div>
            </div>
            <p className={`${styles['turnos-counter']} ${qontactPoint.totalShifts === 0 && styles['empty']}`} style={{cursor: isLite ? 'not-allowed' : 'cursor'}} onClick={() => {
                if (isLite) {
                    showPopup(() => <LiteVersionPopup />);
                    return;
                }
                handleNavigation(`/qontactPoint/?id=${encodeURIComponent(qontactPoint.id)}}`, { qontactPoint, defaultScreen: 'turnos' })
            }}>
                {qontactPoint.totalShifts === 0 ? "Sin turnos" : `${qontactPoint.totalShifts} turnos`}
            </p>
            <div className={styles['qontact-point-actions']}>
                <div className={styles['svg-container']} onClick={() => handleViewQontactPoint(qontactPoint)}>
                    <ViewSVG />
                </div>
                <div className={styles['svg-container']} onClick={() => handleNavigation(`/qontactPoint/?id=${encodeURIComponent(qontactPoint.id)}`, { qontactPoint })}>
                    <EditSVG />
                </div>
                <div className={styles['svg-container']} onClick={() => handleDeleteQontactPoint(qontactPoint)}>
                    <BinSVG />
                </div>
            </div>
        </div>
    );
}

const transformShiftV1Response = (response) => { 
    let finalResult = []
    for (let qontactPointIndex in response){ 
        let qpRow = response[qontactPointIndex]


        let shifts = {}
        if (qpRow.shifts) {
            shifts = qpRow.shifts.weekdayConfiguration
        }

        let shiftsGroupedByName = {}
        for (let day in shifts) {
            for (let shift in shifts[day]) {
                let shiftName = shifts[day][shift].name
                if (!shiftsGroupedByName[shiftName]) {
                    shiftsGroupedByName[shiftName] = []
                }
                shiftsGroupedByName[shiftName].push(day)
            }
        }


        finalResult.push({
            id: qpRow.qontactPoint.id,
            name: qpRow.building.name,
            latitude: qpRow.building.latitude,
            longitude: qpRow.building.longitude,
            buildingID: qpRow.building.id,
            functionalUnitID: qpRow.functionalUnitID,
            totalShifts: Object.keys(shiftsGroupedByName).length,
            triggerDistance: qpRow.qontactPoint.triggerDistance,
            shifts: shifts,
        })
    }
    console.log(finalResult)
    return finalResult
}

const QontactPointListado: React.FC<QontactPointListadoProps> = ({ orgID, isLite, shiftV2, qrMode = 'whatsapp' }) => {
    const { showPopup, hidePopup } = usePopup();
    const [hasMore, setHasMore] = useState<boolean>(true);
    const fetchingRef = useRef(false);
    const controllerRef = useRef<AbortController | null>(null);
    const cancelledRef = useRef(false);

    const navigate = useNavigate();

    const [searchField, setSearchField] = useState<string>('');
    const [qontactPoints, setQontactPoints] = useState<QontactPoint[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const fetchAllQontactPoints = useCallback(async (orgID: string) => {
        if (fetchingRef.current) return;

        controllerRef.current?.abort();
        controllerRef.current = new AbortController();
        const signal = controllerRef.current.signal;

        fetchingRef.current = true;
        cancelledRef.current = false;

        let currentKey = '';
        let initialLoad = true;

        try {
            while (!cancelledRef.current) {

                const qontactPointsService = Application.shared().coreDependencies.qontactPointsService;
                const result = await qontactPointsService.getPaginated(orgID, '50', currentKey, { signal });

                const newItems = transformShiftV1Response(result.data);

                currentKey = result.lastKey;

                setQontactPoints(prev => [...prev, ...newItems]);

                if (result.data.length === 0) {
                    setHasMore(false);
                    break;
                }
                if (initialLoad) {
                    initialLoad = false;
                    setLoading(false);
                }
            }
        } catch (err: any) {
            if (err.name === 'AbortError') {
                console.log('Fetch aborted for orgID:', orgID);
            } else {
                console.error('Fetch error:', err);
            }
        } finally {
            fetchingRef.current = false;
            if (initialLoad) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        console.log('Fetching Qontact Points for orgID:', orgID);
    
        setHasMore(true);
        setLoading(true);
        setQontactPoints([]);
    
        fetchAllQontactPoints(orgID);
    
        return () => {
            console.log('Cleaning up fetch for orgID:', orgID);
            cancelledRef.current = true;
            controllerRef.current?.abort();
            fetchingRef.current = false;
        };
    }, [orgID, fetchAllQontactPoints]);


    const deleteQontactPoint = async (qontactPoint: QontactPoint) => {
        const requestOptions = {
            method: 'DELETE',
            headers: {
                "Content-Type": "application/json",
                "x-access-token": APIIntegration.apiToken,
            }
        };

        setLoading(true);
        try {
            const qontactPointsService = Application.shared().coreDependencies.qontactPointsService;
            await qontactPointsService.deleteQontactPoint(qontactPoint.id);

            showPopup(() => <ResultPopup result={"Qontact Point eliminado!"} succesful={true} />);
            fetchAllQontactPoints(orgID);
        } catch (error) {
            showPopup(() => <ResultPopup result={"Error al eliminar el Qontact Point"} succesful={false} />);
            console.error(error);
        }
        setLoading(false);
    };

    const handleViewQontactPoint = (qontactPoint: QontactPoint) => {
        showPopup(() => <QontactPointDetailModalScreen qontactPoint={qontactPoint} qrMode={qrMode} />, false);
    };

    const handleDeleteQontactPoint = (qontactPoint: QontactPoint) => {
        showPopup(() => <QontactPointDeletePopup qontactPoint={qontactPoint} deleteQontactPoint={deleteQontactPoint} />, false);
    };

    const handleNavigation = (path: string, state: any) => {
        navigate(path, { state });
    };

    // const loadMoreItems = () => { // Use this when you want to scroll load
    //     if (!hasMore) return Promise.resolve();
    //     return fetchQontactPoints();
    // };

    const filteredQontactPoints = qontactPoints.filter(qontactPoint => {
        return qontactPoint.name.toLowerCase().replace(/\s/g, '').includes(searchField.toLowerCase().replace(/\s/g, ''))
    })

    const isItemLoaded = (index: number) => !hasMore || !!filteredQontactPoints[index];

    return (
        <div className={styles['main-container']}>
            <div className={styles['nomina-header']}>
                <div className={styles['svg-container']} style={{ marginRight: '8px', marginLeft: '15px' }}>
                    <QontactPointSVG />
                </div>
                <h2 className={styles['title-nomina']}>
                    Mis Qontact Points
                </h2>

                <div className={styles['search-bar']}>
                    <div className={styles['svg-container']}>
                        <SearchSVG />
                    </div>

                    <div className={styles['search-field-container']}>
                        <input type='text' placeholder='Buscar...' className={styles['search-input']} onChange={e => { setSearchField(e.target.value) }} />
                    </div>
                </div>
            </div>

            <div className={styles['listado-container']}>
                {!loading && qontactPoints.length === 0 &&                         
                    <div className={styles['no-qontact-points']}>
                        <p>No tienes Qontact Points aun!</p>
                    </div>
                }

                {loading &&
                    <div className={styles['no-qontact-points']}>
                        <Loader height="48px" width="48px" />
                    </div>
                }

                {!loading && qontactPoints.length > 0 && (
                    <VirtualizedList
                        itemCount={hasMore ? filteredQontactPoints.length + 1 : filteredQontactPoints.length}
                        isItemLoaded={isItemLoaded}
                        // loadMoreItems={loadMoreItems}
                        itemSize={120}
                        renderRow={(index, style) => {
                            const qontactPoint = filteredQontactPoints[index];

                            if (!qontactPoint) {
                                return <div/>;
                            }

                            return (
                                <QontactPointRow
                                    key={index}
                                    style={style}
                                    index={index}
                                    isLite={isLite}
                                    showPopup={showPopup}
                                    qontactPoint={qontactPoint}
                                    handleNavigation={handleNavigation}
                                    handleViewQontactPoint={handleViewQontactPoint}
                                    handleDeleteQontactPoint={handleDeleteQontactPoint}
                                />
                            );
                            }
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default QontactPointListado
